// Home: "What video will you process today?" — a swipeable row of example videos and what Bump AI
// made from each.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import podcast_thumbnail from '../../assets/images/thumbnail/podcast-thumbnail.jpg';
import llama_surfing_thumbnail from '../../assets/images/thumbnail/llama-bump-thumbnail.jpg';
import school_thumbnail from '../../assets/images/thumbnail/school-thumbnail.jpg';
import youtuber_thumbnail from '../../assets/images/thumbnail/youtuber-thumbnail.jpg';
import education_thumbnail from '../../assets/images/thumbnail/education-thumbnail.jpg';
import legal_thumbnail from '../../assets/images/thumbnail/legal-thumbnail.jpg';
import speech_thumbnail from '../../assets/images/thumbnail/speech-thumbnail.jpg';
import podcast_summary from '../../assets/images/branding/podcast-summary-branding.jpg';
import capabilities from '../../assets/images/branding/capbabilities-branding.jpg';
import study_guide from '../../assets/images/branding/study-guide-branding.jpg';
import challenges_summary from '../../assets/images/branding/mrBeast-challenges-summary-branding.jpg';
import tedtalk_summary from '../../assets/images/branding/tedtalk-summary-thumbnail.jpg';
import legal_lecture from '../../assets/images/branding/legal-lecture-branding.jpg';
import top_insights from '../../assets/images/branding/top-insights-entrepreneur.jpg';

const USE_CASES = [
  { title: 'Podcast', output: 'Podcast summary and analysis', image: podcast_thumbnail, result: podcast_summary },
  { title: 'Any topic', output: 'Capabilities overview', image: llama_surfing_thumbnail, result: capabilities },
  { title: 'Education', output: 'Study guide', image: school_thumbnail, result: study_guide },
  { title: 'YouTuber', output: 'Challenge-by-challenge summary', image: youtuber_thumbnail, result: challenges_summary },
  { title: 'Presentation', output: 'Talk summary', image: education_thumbnail, result: tedtalk_summary },
  { title: 'Legal', output: 'Lecture breakdown', image: legal_thumbnail, result: legal_lecture },
  { title: 'Business', output: 'Top insights', image: speech_thumbnail, result: top_insights },
];

export default function UseCases() {
  const rowRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const updateEdges = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    setEdges({ start: row.scrollLeft <= 4, end: row.scrollLeft + row.clientWidth >= row.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);

  const scrollByCard = dir => {
    const row = rowRef.current;
    const card = row?.firstElementChild;
    if (card) row.scrollBy({ left: dir * (card.offsetWidth + 16), behavior: 'smooth' });
  };

  return (
    <section className="mk-section">
      <div className="mk-container">
        <div className="home-usecases-head">
          <div>
            <p className="mk-eyebrow">Use cases</p>
            <h2 className="mk-h2">What video will you process today?</h2>
          </div>
          <div className="home-usecases-nav">
            <button type="button" className="home-round-btn" onClick={() => scrollByCard(-1)} disabled={edges.start} aria-label="Previous examples">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button type="button" className="home-round-btn" onClick={() => scrollByCard(1)} disabled={edges.end} aria-label="More examples">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>

      <div className="home-usecases-row" ref={rowRef} onScroll={updateEdges}>
        {USE_CASES.map(u => (
          <article key={u.title} className="home-usecase">
            <div className="home-usecase-thumb">
              <img src={u.image} alt={`${u.title} video`} loading="lazy" width="1280" height="720" />
              <span className="home-usecase-tag">{u.title}</span>
            </div>
            <div className="home-usecase-result">
              <p className="home-usecase-label">Bump AI output · {u.output}</p>
              <img src={u.result} alt={`${u.output} generated by Bump AI`} loading="lazy" />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
